import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { dtNL } from "@/lib/format";
import { PageHeader, Card, Badge, Th, Td, Empty } from "./ui";
import { RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RELEVANT = [
  "checkout.session.completed", "checkout.session.expired", "payment_intent.succeeded",
  "payment_intent.payment_failed", "payment_intent.canceled", "charge.refunded",
  "invoice.paid", "invoice.payment_failed", "customer.subscription.created",
  "customer.subscription.updated", "customer.subscription.deleted",
];

export default function StripeEvents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const canReprocess = ["superadmin", "administrator"].includes(user?.role);

  const load = () => api.get("/admin/stripe-events").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const reprocess = async (id) => {
    if (!window.confirm("Event opnieuw verwerken? Idempotent — geen dubbele facturen/berichten.")) return;
    try { const { data } = await api.post(`/admin/stripe-events/${id}/reprocess`); toast.success(data.note || "Verwerkt"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const shown = rows.filter((r) => !q || (r.event_type || "").includes(q) || (r.session_id || "").includes(q));

  return (
    <div>
      <PageHeader title="Stripe events" subtitle="Veilige webhook-logging · geen secrets of volledige betaalgegevens">
        <button data-testid="stripe-refresh" onClick={load} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/10"><RefreshCw size={15} /> Ververs</button>
      </PageHeader>

      <div className="mb-5">
        <input data-testid="stripe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter op event type of session id..." className="w-full max-w-md rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none sm:max-w-sm" />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-white/10"><tr><Th>Event type</Th><Th>Session ID</Th><Th>Betaalstatus</Th><Th>Signature</Th><Th>Pogingen</Th><Th>Status</Th><Th>Ontvangen</Th><Th>Actie</Th></tr></thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} className="border-b border-white/5" data-testid={`stripe-event-${e.id}`}>
                  <Td className="font-medium text-sfc-cyan">{e.event_type}</Td>
                  <Td className="max-w-[160px] truncate font-mono text-xs text-white/50">{e.session_id}</Td>
                  <Td><Badge status={e.payment_status === "paid" ? "paid" : "open"} /></Td>
                  <Td>{e.signature_valid ? <span className="text-emerald-400">geldig</span> : <span className="text-red-400">ongeldig</span>}</Td>
                  <Td>{e.attempts}</Td>
                  <Td><Badge status={e.status === "processed" ? "approved" : "pending"} /></Td>
                  <Td className="text-white/50">{dtNL(e.received_at)}</Td>
                  <Td>{canReprocess && <button onClick={() => reprocess(e.id)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/80 hover:bg-white/10">Reprocess</button>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <Empty text="Nog geen Stripe events. Ze verschijnen zodra webhooks binnenkomen op /api/webhook/stripe." />}
        </div>
      </Card>
      <p className="mt-4 flex items-center gap-2 text-xs text-white/40"><AlertCircle size={13} /> Ondersteunde events: {RELEVANT.join(", ")}.</p>
    </div>
  );
}
